import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import InputAutoComplete from "../../../components/InputAutoComplete";
import "react-day-picker/dist/style.css";
import "../../../styles/calendar.css";
import InputCalender from "../../../components/InputCalender";
import { emitEvent } from "../../../hooks/useEventEmitter";
import ExpandableFilter from "../../../components/ExpandableFilter";
import AdvancedFilter from "../../../components/AdvancedFilter";
import { useCallback, useEffect, useMemo } from "react";
import { formatDate } from "../../../helpers/dateHelper";

const DASHBOARD_FILTER_DRAWER_EVENT = "DASHBOARD_FILTER_DRAWER_EVENT";

interface DashboardFilterProps {
  control: any;
  watch: (field: string) => any;
  quickDays: any;
  otsList: any;
  proceduresList: any;
  surgeonsList: any;
  anesthesiologistList: any;
  circulatingNurseList: any;
  caseStartDelayList: any;
  caseIdListData: any;
  handleReset: () => void;
  selectedAdvancedFilters: any[];
  setSelectedAdvancedFilters: (value: any) => void;
  handleAddFilter: () => void;
  handleDeleteFilter: (index: number) => void;
  setFilterValueDropdown: (value: any) => void;
  setValue: (name: string, value: any) => void;
  isPopulating?: boolean;
  isSurgeon?: boolean;
}

export function toggleDashboardFilterDrawer() {
  emitEvent(DASHBOARD_FILTER_DRAWER_EVENT);
}
const toArray = (val: any) => {
  if (Array.isArray(val)) return val;
  if (val === undefined || val === null || val === "") return [];
  return [val];
};
export const filterConditionsList = [
  {
    key: "notEquals",
    value: "≠",
  },
];

const DashboardFiltersTab: React.FC<DashboardFilterProps> = ({
  control,
  watch,
  quickDays,
  otsList,
  proceduresList,
  surgeonsList,
  anesthesiologistList,
  circulatingNurseList,
  caseStartDelayList,
  caseIdListData,
  handleReset,
  selectedAdvancedFilters,
  setSelectedAdvancedFilters,
  handleAddFilter,
  handleDeleteFilter,
  setFilterValueDropdown,
  setValue,
  isPopulating,
  isSurgeon,
}) => {
  const { t } = useTranslation();

  const watchedOts = watch("ots");
  const watchedProcedureIds = watch("procedureIds");
  const watchedSurgeons = watch("attendingSurgeonNames");
  const watchedAnesthesiologist = watch("assignedAnesthesiologist");
  const watchedCirculatingNurse = watch("nurseIds");
  const watchedCaseStartDelay = watch("caseStartDelay");
  const watchedCaseIds = watch("caseIds");
  const selectedDateRange = watch("dateRange");
  const selectedCaseOrder = watch("caseOrder");
  const selectedAutoClosed = watch("autoClosed");
  const selectedAntibioticUsed = watch("antibioticUsed");
  const selectedInconsistentTimestamps = watch("inconsistentTimestamps");

  const selectedOtsData = useMemo(
    () => watchedOts || [],
    [watchedOts],
  );
  const selectedprocedureIdsData = useMemo(
    () => watchedProcedureIds || [],
    [watchedProcedureIds],
  );
  const selectedSurgeonsData = useMemo(
    () => watchedSurgeons || [],
    [watchedSurgeons],
  );
  const selectedAnesthesiologistData = useMemo(
    () => watchedAnesthesiologist || [],
    [watchedAnesthesiologist],
  );
  const selectedCirculatingNurseData = useMemo(
    () => watchedCirculatingNurse || [],
    [watchedCirculatingNurse],
  );
  const selectedCaseStartDelayData = useMemo(
    () => watchedCaseStartDelay || [],
    [watchedCaseStartDelay],
  );
  const selectedCaseIds = useMemo(
    () => watchedCaseIds || [],
    [watchedCaseIds],
  );

  const getRemainingList = useMemo(
    () =>
      (selectedList: any[], parentList: any[]) => {
        if (isPopulating) return parentList || [];
        if (!Array.isArray(parentList)) return [];

        if (!Array.isArray(selectedList) || selectedList.length === 0) {
          return parentList;
        }

        const selectedSet = new Set(selectedList.map((x) => String(x)));
        return parentList.filter((item) => !selectedSet.has(String(item.key)));
      },
    [isPopulating],
  );

  const selectedOtsList = useMemo(
    () => getRemainingList(selectedOtsData, otsList),
    [selectedOtsData, otsList, getRemainingList],
  );

  const selectedProceduresList = useMemo(
    () => getRemainingList(selectedprocedureIdsData, proceduresList),
    [selectedprocedureIdsData, proceduresList, getRemainingList],
  );

  const selectedSurgeonsList = useMemo(
    () => getRemainingList(selectedSurgeonsData, surgeonsList),
    [selectedSurgeonsData, surgeonsList, getRemainingList],
  );

  const selectedAnesthesiologistList = useMemo(
    () => getRemainingList(selectedAnesthesiologistData, anesthesiologistList),
    [selectedAnesthesiologistData, anesthesiologistList, getRemainingList],
  );

  const selectedCaseStartDelayList = useMemo(
    () => getRemainingList(selectedCaseStartDelayData, caseStartDelayList),
    [selectedCaseStartDelayData, caseStartDelayList, getRemainingList],
  );

  const selectedCirculatingNurseList = useMemo(
    () => getRemainingList(selectedCirculatingNurseData, circulatingNurseList),
    [selectedCirculatingNurseData, circulatingNurseList, getRemainingList],
  );

  const selectedCaseIdsList = useMemo(
    () => getRemainingList(selectedCaseIds, caseIdListData),
    [selectedCaseIds, caseIdListData, getRemainingList],
  );

  const caseAutoClosedList = useMemo(
    () => [
      { key: "true", value: t("Yes") },
      { key: "false", value: t("No") },
    ],
    [t],
  );

  const antibioticUsedList = useMemo(
    () => [
      { key: "true", value: t("Yes") },
      { key: "false", value: t("No") },
    ],
    [t],
  );

  const caseOrderList = useMemo(() => {
    return [
      { key: "first", value: t("case_order_first") },
      { key: "last", value: t("case_order_last") },
    ];
  }, [t]);

  const filterField = useMemo(() => {
    const fields = [
      {
        value: "OT",
        key: "otNames",
      },

      {
        value: "Primary Procedure",
        key: "procedureIds",
      },
      {
        value: "Primary Surgeon",
        key: "attendingSurgeonNames",
      },
      {
        value: "Circulating Nurse",
        key: "circulatingNurses",
      },
      {
        value: "Anesthesiologist",
        key: "anesthesiologists",
      },
      {
        value: "Case Start Delay",
        key: "caseStartDelays",
      },
      {
        value: "Case Order",
        key: "caseOrder",
      },
      {
        value: "Case ID",
        key: "caseIds",
      },
    ];

    return isSurgeon
      ? fields.filter((field) => field.key !== "attendingSurgeonNames")
      : fields;
  }, [isSurgeon]);

  const filterValueDropdownOptions = useMemo(() => {
    return [
      { key: "caseIds", value: selectedCaseIdsList },
      { key: "otNames", value: selectedOtsList },
      { key: "procedureIds", value: selectedProceduresList },
      { key: "attendingSurgeonNames", value: selectedSurgeonsList },
      { key: "circulatingNurses", value: selectedCirculatingNurseList },
      { key: "anesthesiologists", value: selectedAnesthesiologistList },
      { key: "caseStartDelays", value: selectedCaseStartDelayList },
      { key: "caseOrder", value: caseOrderList, isMulti: false },
    ];
  }, [
    selectedOtsList,
    selectedProceduresList,
    selectedSurgeonsList,
    selectedCirculatingNurseList,
    selectedAnesthesiologistList,
    selectedCaseStartDelayList,
    caseOrderList,
    selectedCaseIdsList,
  ]);

  useEffect(() => {
    setFilterValueDropdown(filterValueDropdownOptions);
  }, [filterValueDropdownOptions, setFilterValueDropdown]);

  const getLabels = useCallback((selectedKeys: any[], list: any[]) => {
    if (!Array.isArray(selectedKeys) || selectedKeys.length === 0) return [];

    return selectedKeys
      .map((k) => {
        // Loose equality to handle string/number mismatch
        const found = list.find((item) => String(item.key) === String(k));
        return found?.value ?? String(k); // ✅ fallback to raw
      })
      .filter((x) => !!String(x).trim());
  }, []);

  const getSingleLabel = useCallback((selectedKey: any, list: any[]) => {
    if (selectedKey === undefined || selectedKey === null || selectedKey === "")
      return null;
    const found = list.find((item) => String(item.key) === String(selectedKey));
    return found?.value ?? String(selectedKey); // ✅ fallback to raw
  }, []);

  const hasValue = useCallback((v: any) => v !== undefined && v !== null && v !== "", []);

  const addIfArray = useCallback((target: any, key: string, values: any[]) => {
    if (Array.isArray(values) && values.length) target[key] = values;
  }, []);

  const mapAdvancedFilters = useCallback((filters: any[]) => {
    if (!Array.isArray(filters)) return [];
    return filters
      .filter(
        (filter) =>
          (Array.isArray(filter?.selectedValues) &&
            filter.selectedValues.length > 0) ||
          (filter?.selectedValues !== undefined &&
            filter?.selectedValues !== null &&
            filter?.selectedValues !== "" &&
            !Array.isArray(filter?.selectedValues)),
      )
      .map((filter) => {
        let list: any[] = [];
        switch (filter.filterType) {
          case "otNames":
            list = otsList;
            break;
          case "procedureIds":
            list = proceduresList;
            break;
          case "attendingSurgeonNames":
          case "primarySurgeons":
            list = surgeonsList;
            break;
          case "circulatingNurses":
            list = circulatingNurseList;
            break;
          case "anesthesiologists":
            list = anesthesiologistList;
            break;
          case "caseStartDelays":
            list = caseStartDelayList;
            break;
          case "caseOrder":
            list = caseOrderList;
            break;
          default:
            return filter;
        }

        if (list && list.length > 0) {
          if (Array.isArray(filter.selectedValues)) {
            const namedValues = getLabels(filter.selectedValues, list);
            if (namedValues.length > 0) {
              return { ...filter, selectedValues: namedValues };
            }
          } else {
            const namedValue = getSingleLabel(filter.selectedValues, list);
            if (namedValue) {
              return { ...filter, selectedValues: namedValue };
            }
          }
        }
        return filter;
      })
      .filter(Boolean);
  }, [
    anesthesiologistList,
    caseOrderList,
    caseStartDelayList,
    circulatingNurseList,
    getLabels,
    getSingleLabel,
    otsList,
    proceduresList,
    surgeonsList,
  ]);

  const currentFilters = useMemo(() => {
    const filters: any = {};

    addIfArray(filters, "otNames", getLabels(selectedOtsData, otsList));
    addIfArray(
      filters,
      "procedureIds",
      getLabels(selectedprocedureIdsData, proceduresList),
    );
    addIfArray(filters, "primarySurgeons", getLabels(selectedSurgeonsData, surgeonsList));
    addIfArray(
      filters,
      "circulatingNurses",
      getLabels(selectedCirculatingNurseData, circulatingNurseList),
    );
    addIfArray(
      filters,
      "anesthesiologists",
      getLabels(selectedAnesthesiologistData, anesthesiologistList),
    );
    addIfArray(
      filters,
      "caseStartDelays",
      getLabels(selectedCaseStartDelayData, caseStartDelayList),
    );

    if (selectedCaseOrder) {
      filters.caseOrder = getSingleLabel(selectedCaseOrder, caseOrderList);
    }

    if (selectedDateRange?.from && selectedDateRange?.to) {
      filters.dateRange = selectedDateRange;
    }

    if (selectedCaseIds?.length) {
      filters.caseIds = getLabels(selectedCaseIds, caseIdListData);
    }

    if (selectedAutoClosed) {
      filters.autoClosed = getSingleLabel(
        selectedAutoClosed,
        caseAutoClosedList,
      );
    }

    if (hasValue(selectedAntibioticUsed)) {
      filters.antibioticUsed = getSingleLabel(
        selectedAntibioticUsed,
        antibioticUsedList,
      );
    }

    if (hasValue(selectedInconsistentTimestamps)) {
      filters.inconsistentTimestamps = getSingleLabel(
        selectedInconsistentTimestamps,
        antibioticUsedList,
      );
    }

    const mappedAdvancedFilters = mapAdvancedFilters(selectedAdvancedFilters);
    if (mappedAdvancedFilters.length > 0) {
      filters.advanceFilters = mappedAdvancedFilters;
    }

    return filters;
  }, [
    addIfArray,
    getLabels,
    getSingleLabel,
    hasValue,
    mapAdvancedFilters,
    selectedOtsData,
    otsList,
    selectedprocedureIdsData,
    proceduresList,
    selectedSurgeonsData,
    surgeonsList,
    selectedCirculatingNurseData,
    circulatingNurseList,
    selectedAnesthesiologistData,
    anesthesiologistList,
    selectedCaseStartDelayData,
    caseStartDelayList,
    selectedDateRange,
    selectedCaseOrder,
    caseOrderList,
    selectedCaseIds,
    caseIdListData,
    selectedAutoClosed,
    caseAutoClosedList,
    selectedAntibioticUsed,
    antibioticUsedList,
    selectedInconsistentTimestamps,
    selectedAdvancedFilters,
  ]);
  type ChipItem = { key: string; value: string | number; index?: number };

  // already have this:
  const appliedFiltersForUI = useMemo<ChipItem[]>(() => {
    const chips: ChipItem[] = [];

    if (currentFilters.dateRange?.from && currentFilters.dateRange?.to) {
      const fromStr = formatDate(currentFilters.dateRange.from);
      const toStr = formatDate(currentFilters.dateRange.to);
      chips.push({ key: "dateRange", value: `${fromStr} - ${toStr}` });
    }

    (currentFilters.otNames ?? []).forEach((v: string) =>
      chips.push({ key: "otNames", value: v }),
    );
    (currentFilters.procedureIds ?? []).forEach((v: string) =>
      chips.push({ key: "procedureIds", value: v }),
    );
    (currentFilters.primarySurgeons ?? []).forEach((v: string) =>
      chips.push({ key: "primarySurgeons", value: v }),
    );
    (currentFilters.circulatingNurses ?? []).forEach((v: string) =>
      chips.push({ key: "circulatingNurses", value: v }),
    );
    (currentFilters.anesthesiologists ?? []).forEach((v: string) =>
      chips.push({ key: "anesthesiologists", value: v }),
    );
    (currentFilters.caseStartDelays ?? []).forEach((v: string) =>
      chips.push({ key: "caseStartDelays", value: v }),
    );
    (currentFilters.caseIds ?? []).forEach((v: string) =>
      chips.push({ key: "caseIds", value: v }),
    );

    if (currentFilters.caseOrder) {
      chips.push({ key: "caseOrder", value: String(currentFilters.caseOrder) });
    }
    if (currentFilters.autoClosed) {
      chips.push({
        key: "autoClosed",
        value: String(currentFilters.autoClosed),
      });
    }

    if (currentFilters.antibioticUsed) {
      chips.push({
        key: "antibioticUsed",
        value: String(currentFilters.antibioticUsed),
      });
    }

    if (currentFilters.inconsistentTimestamps) {
      chips.push({
        key: "inconsistentTimestamps",
        value: String(currentFilters.inconsistentTimestamps),
      });
    }

    return chips;
  }, [currentFilters]);

  const advancedFiltersOnly = useMemo(() => {
    const af = currentFilters?.advanceFilters;
    const hasValid =
      Array.isArray(af) &&
      af.some((f: any) => {
        const v = f?.selectedValues;
        return (
          (Array.isArray(v) && v.length > 0) ||
          (v !== undefined && v !== null && v !== "" && !Array.isArray(v))
        );
      });
    return hasValid ? { advanceFilters: af } : {};
  }, [currentFilters]);

  const hasAppliedFilters = useMemo(
    () => appliedFiltersForUI.length > 0,
    [appliedFiltersForUI],
  );

  const hasAdvancedFilters = useMemo(() => {
    return Object.keys(advancedFiltersOnly).length > 0;
  }, [advancedFiltersOnly]);

  const clearAdvancedFilters = () => {
    const count = selectedAdvancedFilters.length;
    if (!count) return;
    for (let i = count - 1; i >= 0; i--) {
      handleDeleteFilter(i);
    }
  };

  const data = useMemo(() => {
    const list: any[] = [];

    if (hasAppliedFilters) {
      list.push({
        main_title: "Applied Filters",
        button_title: "Clear Applied Filters",
        isAdvanced: false,
        filters: currentFilters,
      });
    }

    if (hasAdvancedFilters) {
      list.push({
        main_title: "Advanced Filters",
        button_title: "Clear Advanced Filters",
        isAdvanced: true,
        filters: advancedFiltersOnly,
      });
    }

    return list;
  }, [advancedFiltersOnly, hasAppliedFilters, hasAdvancedFilters, currentFilters]);

  const findKeyByValue = (list: any[], val: string | number) =>
    list.find((item) => String(item.value) === String(val))?.key;

  const ADV_LIST_BY_TYPE: Record<string, any[]> = {
    otNames: otsList,
    procedureIds: proceduresList,
    attendingSurgeonNames: surgeonsList,
    primarySurgeons: surgeonsList,
    circulatingNurses: circulatingNurseList,
    anesthesiologists: anesthesiologistList,
    caseStartDelays: caseStartDelayList,
    caseOrder: caseOrderList,
  };

  const FIELD_MAP: Record<string, { fieldName: string; list: any[] }> = {
    otNames: { fieldName: "ots", list: otsList },
    procedureIds: { fieldName: "procedureIds", list: proceduresList },
    primarySurgeons: { fieldName: "attendingSurgeonNames", list: surgeonsList },
    circulatingNurses: { fieldName: "nurseIds", list: circulatingNurseList },
    anesthesiologists: {
      fieldName: "assignedAnesthesiologist",
      list: anesthesiologistList,
    },
    caseStartDelays: { fieldName: "caseStartDelay", list: caseStartDelayList },
    caseIds: { fieldName: "caseIds", list: caseIdListData },
  };

  const clearSingleField = (k: string) => {
    if (k === "dateRange")
      return setValue("dateRange", { from: null, to: null });
    if (k === "caseOrder") return setValue("caseOrder", null);
    if (k === "autoClosed") return setValue("autoClosed", null);
    if (k === "antibioticUsed") return setValue("antibioticUsed", null);
    if (k === "inconsistentTimestamps")
      return setValue("inconsistentTimestamps", null);
  };

  const removeFromMultiField = (key: string, displayValue: string | number) => {
    const mapping = FIELD_MAP[key];
    if (!mapping) return;

    const itemKey = findKeyByValue(mapping.list, displayValue);
    if (!itemKey) return;

    const raw = watch(mapping.fieldName);
    const currentValues = toArray(raw);
    setValue(
      mapping.fieldName,
      currentValues.filter((v: any) => v !== itemKey),
    );
  };

  const removeFromAdvancedFilter = (
    displayValue: string | number,
    index?: number,
  ) => {
    if (index === undefined) return;

    const filter = selectedAdvancedFilters[index];
    if (!filter) {
      handleDeleteFilter(index);
      return;
    }

    const list = ADV_LIST_BY_TYPE[filter.filterType] || [];
    const itemKey = findKeyByValue(list, displayValue);

    if (itemKey !== undefined) {
      const fieldName = `options_${index}`;

      const raw = watch(fieldName);
      const currentValues = toArray(raw);

      const newValues = currentValues.filter((v: any) => v !== itemKey);

      if (newValues.length === 0 && selectedAdvancedFilters.length > 1) {
        handleDeleteFilter(index);
      } else if (newValues.length === 0) {
        setValue(fieldName, []);
        setValue(`type_${index}`, "");
        setValue(`condition_${index}`, "");

        setSelectedAdvancedFilters((prev: any) => {
          const updated = [...prev];
          updated[index] = {
            filterType: "",
            condition: "",
            selectedValues: [],
          };
          return updated;
        });
      } else {
        setValue(fieldName, newValues);
      }
      return;
    }

    handleDeleteFilter(index);
  };

  const handleChipDelete = (
    key: string,
    value: string | number,
    index?: number,
  ) => {
    if (isSurgeon && key === "primarySurgeons") return;

    if (key === "advanceFilters") {
      removeFromAdvancedFilter(value, index);
      return;
    }

    // single-field clears
    if (
      key === "dateRange" ||
      key === "caseOrder" ||
      key === "autoClosed" ||
      key === "antibioticUsed" ||
      key === "inconsistentTimestamps"
    ) {
      clearSingleField(key);
      return;
    }

    // normal multi filters
    removeFromMultiField(key, value);
  };

  return (
    <Box>
      {data?.map((filter) => {
        const onReset =
          filter.isAdvanced === true ? clearAdvancedFilters : handleReset;
        return (
          <ExpandableFilter
            key={filter.main_title}
            main_title={filter.main_title}
            button_title={filter.button_title}
            isAdvanced={filter.isAdvanced}
            filters={filter.filters}
            handleReset={onReset}
            onChipDelete={handleChipDelete}
          />
        );
      })}
      {data.length > 0 && (
        <Box sx={{ border: 0.5, mt: 2, borderColor: "#999999" }} />
      )}
      <Box mt={3} gap={2} display="flex" flexDirection="column">
        <InputCalender
          name="dateRange"
          control={control}
          label={t("Date")}
          defaultQuickDays={quickDays}
          enableQuickSelect
        />
        <InputAutoComplete
          name="ots"
          control={control}
          label={t("OT Name")}
          options={otsList}
        />
        <InputAutoComplete
          name="procedureIds"
          control={control}
          label={t("Procedure_Name")}
          options={proceduresList}
          enableDynamicSearch
        />
        <InputAutoComplete
          name="attendingSurgeonNames"
          control={control}
          label={t("Primary_Surgeon")}
          options={surgeonsList}
          isDisabled={isSurgeon}
        />
        <InputAutoComplete
          name="nurseIds"
          control={control}
          label={t("Circulating Nurse")}
          options={circulatingNurseList}
        />
        <InputAutoComplete
          name="assignedAnesthesiologist"
          control={control}
          label={t("Anesthesiologist")}
          options={anesthesiologistList}
        />
        <InputAutoComplete
          name="caseOrder"
          control={control}
          label={t("Case_Order")}
          options={caseOrderList}
          isMulti={false}
        />
        <InputAutoComplete
          name="caseStartDelay"
          control={control}
          label={t("Case_Start_Delay")}
          options={caseStartDelayList}
        />
        <InputAutoComplete
          name="caseIds"
          control={control}
          label={t("Case ID")}
          options={caseIdListData}
          isMulti={true}
        />
        <InputAutoComplete
          name="autoClosed"
          control={control}
          label={t("Case_Auto_Closed")}
          options={caseAutoClosedList}
          isMulti={false}
        />
        <InputAutoComplete
          name="antibioticUsed"
          control={control}
          label={t("Antibiotic_Used")}
          options={antibioticUsedList}
          isMulti={false}
        />
        <InputAutoComplete
          name="inconsistentTimestamps"
          control={control}
          label={t("Inconsistent_Timestamps")}
          options={antibioticUsedList}
          isMulti={false}
        />
        <AdvancedFilter
          main_title={t("Advanced_Filters")}
          filterField={filterField}
          filterConditions={filterConditionsList}
          filterValueDropdown={filterValueDropdownOptions}
          selectedAdvancedFilters={selectedAdvancedFilters}
          handleAddFilter={handleAddFilter}
          handleDeleteFilter={handleDeleteFilter}
          control={control}
        />
      </Box>
    </Box>
  );
};

export default DashboardFiltersTab;
